// composables/useAuth.js
import { useUserStore } from '~/stores/user';

export const useAuth = (supabase, userStore) => {
  const restoreSession = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session) {
      localStorage.setItem('session', JSON.stringify(session));
      await userStore.fetchUserAndProfile(session);
    } else {
      userStore.user.id = '';
      userStore.user.is_anonymous = false;
      localStorage.removeItem('session');
    }
  };

  const handleAuthStateChange = async (event, session) => {
    if (event === 'SIGNED_IN') {
      localStorage.setItem('session', JSON.stringify(session));
      await userStore.fetchUserAndProfile(session);
    } else if (event === 'SIGNED_OUT') {
      userStore.user.id = '';
      userStore.user.is_anonymous = false;
      localStorage.removeItem('session');
    }
  };

  /**
   * Start a guest session — or RESUME the one already running.
   *
   * The resume check is not an optimisation, it prevents data loss.
   * supabase.auth.signInAnonymously() ALWAYS mints a brand-new anonymous user;
   * there is no "sign in to the existing anonymous account" — the session token
   * is the only handle on it. So calling it while a guest session is already
   * live swaps localStorage over to an empty new user and permanently orphans
   * everything the previous guest made. It cannot be recovered afterwards,
   * because you cannot authenticate as an anonymous user without its token.
   *
   * The visible symptom was the login screen reporting "1 card" on one visit
   * and "0 cards" on the next: the count was accurate, it was just describing
   * a different, newly created guest.
   *
   * "Continue as Guest" sits on both the login and signup tabs, and both are
   * reachable while a guest session is active, so the guard belongs here rather
   * than in a v-if on any one button.
   */
  async function continueAsGuest() {
    const userStore = useUserStore();

    const {
      data: { session: existing },
    } = await supabase.auth.getSession();

    if (existing?.user) {
      // Already a guest: hand back the SAME session, cards and all.
      // Already a member: never downgrade them into an empty guest session —
      // clicking this by accident would otherwise sign them out of their own
      // account.
      await userStore.fetchUserAndProfile(existing);
      return { data: { session: existing }, resumed: true };
    }

    const { data, error } = await supabase.auth.signInAnonymously();

    if (error) {
      console.error('Anonymous sign-in failed:', error.message);
      return { error };
    }

    // A client-side `profiles.ip_address` write used to happen here. It cannot
    // work and never did: ip_address has no GRANT UPDATE to `authenticated`, so
    // the statement is a hard 42501, and the pinning trigger would blank it
    // anyway. `.update()` returns the error rather than throwing, so the catch
    // below never fired and the failure was invisible. Recording a guest's IP
    // needs a service-role path (an edge function), not a client write.

    await userStore.fetchUserAndProfile(data.session);

    return { data };
  }

  async function convertGuestToAccount({ email, password, name }) {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.updateUser({ email, password });
    if (authError) return { error: authError };

    if (name) {
      // `email` is deliberately not written here: it has no GRANT UPDATE to
      // `authenticated` and is pinned by protect_profile_privileged_columns,
      // so including it made the whole statement fail with 42501 and aborted
      // the conversion. auth.users.email is the source of truth; syncing
      // profiles.email needs the service role (the sign-up edge function).
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ name })
        .eq('id', user.id);
      if (profileError) return { error: profileError };
    }

    return { data: user };
  }

  async function convertGuestToOAuth(provider) {
    const { data, error } = await supabase.auth.linkIdentity({ provider });
    if (error) return { error };
    return { data };
  }

  /**
   * Move a guest session's content into an account that already exists.
   *
   * Only for the SIGN-IN path. Signing up converts the anonymous user in place
   * (see the sign-up edge function), so the uid never changes and there is
   * nothing to move.
   *
   * Order matters and is easy to get wrong: signInWithPassword REPLACES the
   * stored session, so the guest token has to be captured before signing in.
   * Callers therefore pass it in rather than reading it here.
   *
   * @param {string} guestAccessToken captured BEFORE the sign-in call
   * @returns {Promise<{data?: object, error?: object}>}
   */
  async function mergeGuestInto(guestAccessToken) {
    if (!guestAccessToken) return { data: { moved: {} } };

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session || session.user?.is_anonymous) {
      return { error: { message: 'Sign in first.' } };
    }

    const { data, error } = await supabase.functions.invoke('merge-guest', {
      body: { guestAccessToken },
    });
    if (error) return { error };
    return { data };
  }

  function isAnonymousUser(session) {
    return session?.user?.is_anonymous === true;
  }

  return {
    restoreSession,
    handleAuthStateChange,
    continueAsGuest,
    convertGuestToAccount,
    convertGuestToOAuth,
    mergeGuestInto,
    isAnonymousUser,
  };
};
